import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { getMenu, getSetting, getRoleMap, getTheme } from 'pc-sales/store/actions/system';
import { signOut } from 'pc-sales/store/actions/user';
import { getOverview, getAllManualType } from 'pc-sales/store/actions/notice';
import Clipboard from 'common/components/Clipboard';
import shortcuts from './shortcuts';
import loaders from '../loaders';
import formatter from 'common/utils/formatter';
import { languages } from 'common/i18n';
import Loading from 'common/components/Loading';
import AffixConfig from './Affix';
import statics from 'common/statics';

// todo: split code

/**
 * Resolve a theme image value (from /v2/platform/setting/theme) to a plain URL string.
 * / 把主题接口返回的图像值解成纯 URL 字符串。
 *
 * Field priority matches Design page's resolvePreviewUrl and crm-admin App/Init's
 * resolveThemeImageUrl:
 * / 字段优先级（与 Design 页 resolvePreviewUrl 和 crm-admin App/Init 页 resolveThemeImageUrl 保持一致）：
 *   1. oss (after upload, BFF has rewritten it to a relative path that the gateway CDN serves)
 *   2. aws (only when still absolute — the BFF may rewrite it after upload)
 *   3. storageUrl (signed gateway URL — last resort before going blank)
 *   4. uri (raw resource URI from the upload service)
 */
function resolveThemeImageUrl(stored: any): string {
    if (stored == null) return '';
    if (typeof stored === 'string') return stored;
    if (typeof stored !== 'object') return '';
    const inner = stored.url && typeof stored.url === 'object' ? stored.url : stored;
    if (typeof inner.oss === 'string' && inner.oss) return inner.oss;
    if (typeof inner.aws === 'string' && /^https?:\/\//i.test(inner.aws)) return inner.aws;
    if (typeof inner.storageUrl === 'string' && inner.storageUrl) return inner.storageUrl;
    return inner.uri || '';
}

const mapStateToProps = (state: any) => ({
    // oldLink: state.system.get('oldLink'),
    menu: state.system.get('menu'),
    company: state.system.getIn(['setting', 'PlatForm_Company']),
    security: state.system.getIn(['setting', 'Passport_Security']),
    time: state.system.getIn(['setting', 'Crm_Time']),
    // Header logo now comes from the theme bundle (PlatForm_Company.logo is no longer the source).
    // 头部 logo 改为主题包里的 mainLogo，不再取 PlatForm_Company.logo。
    themeLight: state.system.getIn(['theme', 'light']),
    // notice: state.notice.get('noticeOverview'),
    isLoadingNotice: state.notice.get('isLoading'),
    user: state.user.get('status'),
});

const mapDispatchToProps = (dispatch: any) => ({
    ...bindActionCreators(
        {
            getMenu: getMenu.init,
            getSetting: getSetting.init,
            signOut: signOut.init,
            getRoleMap: getRoleMap.init,
            // Pull theme images (favicon/mainLogo/squareLogo/clientLoginBg) for the header.
            // 拉取主题图片（favicon/mainLogo/squareLogo/clientLoginBg）用于头部 logo。
            getTheme: getTheme.init,
            // getOldLink: system.getOldLink,
            getNoticeList: getOverview.init,
            getAllManualType: getAllManualType.init,
        },
        dispatch,
    ),
});

interface iProps {
    menu: any;
    company: any;
    security: any;
    time: any;
    themeLight: any;
    notice: any;
    user: any;
    oldLink: any;
    getOldLink: any;
    getMenu: Function;
    getSetting: Function;
    getNoticeList: Function;
    getRoleMap: Function;
    getTheme: Function;
    signOut: Function;
    clipboard: any;
    getAllManualType: Function;
    isLoadingNotice: boolean;
}

export default (WrappedComponent: React.ComponentType<any>): any => {
    @Clipboard()
    @(connect(mapStateToProps, mapDispatchToProps) as any)
    class Service extends React.Component<iProps> {
        /**
         * 判断数据是否加载完成
         * @returns {any}
         */
        isReady = () => {
            const { menu, company, time, security, isLoadingNotice } = this.props;
            return !isLoadingNotice && menu && company && time && security;
        };

        /**
         * 时间格式化工具初始化
         * @param time 系统时间设置
         */
        setFormatter = (time: any) => {
            const { timeZone, summerTime, timeFormat } = time;
            formatter.time.set({
                timezone: Number(timeZone),
                isDST: summerTime,
                pattern: timeFormat,
            });
        };

        /**
         * 获取快捷方式设置
         * @returns {any[]}
         */
        getShortcuts = () => {
            const { menu, signOut, time, user, clipboard } = this.props;
            let menuConfig = menu.find(({ key }: any) => key === 'PERSONAL');
            let children = menuConfig && menuConfig.children ? menuConfig.children : [];
            const clock = {
                timezone: Number(time.timeZone),
                isDST: time.summerTime,
                format: time.timeFormat,
            };
            return [
                shortcuts.Clock({ lang: languages.getDefault(), clock }),
                shortcuts.Lang(),
                shortcuts.Notice(),
                shortcuts.Account({
                    children: children && children[0] ? children[0].children : [],
                    signOut,
                    uuid: user.uuid,
                    copy: clipboard.copy,
                }),
            ];
        };

        /**
         * 获取路由配置
         */
        getConfig = () => {
            const { menu, company, time, themeLight } = this.props;
            return {
                roleType: 'Sales',
                lang: languages.getDefault(),
                logo: {
                    // Header logo now sourced from the theme bundle (/v2/platform/setting/theme).
                    // PlatForm_Company.logo is no longer the source — Company page no longer exposes
                    // logo, and the Design page is the authoritative place for branding assets.
                    // 头部 logo 改为主题包（/v2/platform/setting/theme）的主 logo。
                    // PlatForm_Company.logo 不再作为来源：Company 页面已移除 logo 字段，
                    // Design 页面是品牌资产的唯一来源。
                    url: resolveThemeImageUrl(themeLight?.mainLogo),
                    alt: company.company,
                    website:
                        company.website && company.website.indexOf('://') == -1
                            ? formatter.contact.fillUrl(company.website)
                            : company.website,
                },
                routesConfig: {
                    routes: menu,
                    loaders,
                },
                affixConfig: <AffixConfig />,
                shortcutsConfig: this.getShortcuts(),
                clock: {
                    timezone: Number(time.timeZone),
                    isDST: time.summerTime,
                    format: time.timeFormat,
                },
                // timing: {
                //     timeout: parseInt(security.overtimeLogout) * 60 * 1000,
                //     action: signOut,
                // },
                copyright: {
                    company: company.company,
                },
            };
        };

        /**
         * 是否获取旧版系统链接
         * @returns {boolean}
         */
        isGetOldLink = () => {
            const { oldLink, user } = this.props;
            return user.source === 'import' && !oldLink;
        };

        componentDidMount() {
            const { getMenu, getSetting, getOldLink, getRoleMap, getNoticeList, getAllManualType, getTheme } =
                this.props;
            // 获取菜单
            getMenu();
            // 获取系统设置
            getSetting({ keys: ['Crm_Time', 'Passport_Security'] });
            getRoleMap({ utype: 'sales' });
            // 获取通知列表
            getNoticeList({ page: 1, pagesize: 5, init: true, state: 0 });
            // 获取系统入口地址
            this.isGetOldLink() && getOldLink();
            // 获取地区配置
            statics.initData(['location']);
            // 获取含有子级的所有通知类型列表
            getAllManualType({ language: languages.getDefault() });
            // 拉取主题图片（浅色模式），用于顶部 logo
            getTheme({ mode: 'light' });
        }

        UNSAFE_componentWillReceiveProps(nextProps: iProps) {
            const { time } = nextProps;
            time && this.setFormatter(time);
        }

        render() {
            return this.isReady() ? <WrappedComponent {...this.props} config={this.getConfig()} /> : <Loading />;
        }
    }
    return Service;
};
